import { SkeletonLoader } from '@payaca/ambrosia';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, ReactNode } from 'react';

export type PageSkeletonProps = {
  className?: string;
  content?: ReactNode;
};

const SidebarSkeleton: FC = () => (
  <aside
    // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values
    className="hidden md:grid p-4 pr-2 w-60 min-h-0 grid-rows-[auto_1fr_auto] overflow-hidden"
    style={{ gridArea: 'sidebar' }}
  >
    <div className="flex items-center gap-2 mb-4 justify-between">
      <SkeletonLoader className="h-10 flex-1 rounded-lg" />
      <SkeletonLoader.Circle className="h-10 w-10 shrink-0" />
    </div>

    <div className="space-y-1">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2">
          <SkeletonLoader.Square className="h-5 w-5 rounded" />
          <SkeletonLoader.Text
            className={clstx('h-4', i % 2 === 0 ? 'w-28' : 'w-24')}
          />
        </div>
      ))}
    </div>

    <div className="mt-auto space-y-1 pt-4">
      <div className="flex items-center gap-3 p-2">
        <SkeletonLoader.Square className="h-5 w-5 rounded" />
        <SkeletonLoader.Text className="h-4 w-24" />
      </div>
      <div className="flex items-center gap-3 p-2">
        <SkeletonLoader.Square className="h-5 w-5 rounded" />
        <SkeletonLoader.Text className="h-4 w-20" />
      </div>
      <div className="flex items-center gap-3 p-2 mt-2 border-t">
        <SkeletonLoader.Circle className="h-8 w-8 shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <SkeletonLoader.Text className="h-3 w-24" />
          <SkeletonLoader.Text className="h-3 w-16" />
        </div>
      </div>
    </div>
  </aside>
);

const TopBarSkeleton: FC = () => (
  <header className="flex justify-between items-center gap-4 p-3 bg-white border-b border-gray-200">
    <SkeletonLoader.Square className="h-8 w-8 rounded" />
    <div className="flex items-center gap-2 grow">
      <SkeletonLoader.Text className="h-4 w-32" />
    </div>
    <SkeletonLoader className="h-8 w-24 rounded-lg" />
  </header>
);

const ContentSkeleton: FC = () => (
  <div className="p-8 flex flex-col gap-6 max-w-screen-xl">
    <SkeletonLoader className="h-8 w-1/3 rounded" />

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <SkeletonLoader className="h-28 w-full rounded-lg" />
      <SkeletonLoader className="h-28 w-full rounded-lg" />
      <SkeletonLoader className="h-28 w-full rounded-lg" />
    </div>

    <div className="space-y-3">
      <SkeletonLoader.Text className="w-full" />
      <SkeletonLoader.Text className="w-11/12" />
      <SkeletonLoader.Text className="w-4/5" />
      <SkeletonLoader.Text className="w-3/4" />
    </div>
  </div>
);

const PageSkeleton: FC<PageSkeletonProps> = (props) => {
  const { className, content = <ContentSkeleton /> } = props;

  return (
    <div
      className={clstx('grid h-full overflow-hidden', className)}
      style={{
        gridTemplateRows: '1fr',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gridTemplateAreas: '"sidebar content"',
      }}
      aria-busy="true"
      aria-label="Loading page"
    >
      <SidebarSkeleton />

      <div
        className="grid min-w-0 min-h-0 m-2 bg-white rounded-lg border overflow-hidden"
        style={{ gridArea: 'content', gridTemplateRows: 'auto 1fr' }}
      >
        <TopBarSkeleton />
        <div className="overflow-y-auto min-h-0">{content}</div>
      </div>
    </div>
  );
};

export default PageSkeleton;
